import { z } from 'zod';

export const HotelPensionTypeEnum = z.enum([
  'SingleRoom',
  'Breakfast',
  'SemiPension',
  'FullPension',
  'AllInclusive',
]);

/** Coerces string from number inputs to number | null for form validation */
const priceField = z
  .union([z.null(), z.number(), z.string()])
  .optional()
  .transform((val) => {
    if (val === '' || val === null || val === undefined) return null;
    const n = Number(val);
    return Number.isNaN(n) ? null : n;
  });

export const HotelPensionPriceSchema = z.object({
  id: z.string().optional(),
  pensionType: HotelPensionTypeEnum,
  adultPrice: priceField,
  childPrice: priceField,
  adolescentPrice: priceField,
});

const toDateOrNull = (val: Date | string | null | undefined): Date | null => {
  if (val == null) return null;
  if (val instanceof Date) return val;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const HotelPensionPricingPeriodSchema = z
  .object({
    id: z.string().optional(),
    startDate: z.union([z.null(), z.string(), z.date()]).optional(),
    endDate: z.union([z.null(), z.string(), z.date()]).optional(),
    applicationDays: z.array(z.string()).optional().default([]),
  })
  .refine(
    (data) => {
      const start = toDateOrNull(data.startDate);
      const end = toDateOrNull(data.endDate);
      if (!start || !end) return true;
      return start <= end;
    },
    { message: 'Les dates de définition de période sont invalides.' },
  );

export const HotelPensionPricingSchema = z
  .object({
    id: z.string().optional(),
    name: z.union([z.null(), z.string()]).optional(),
    periods: z.array(HotelPensionPricingPeriodSchema).optional().default([]),
    prices: z.array(HotelPensionPriceSchema).optional().default([]),
  })
  .refine(
    (data) => {
      const periods = data.periods ?? [];
      if (periods.length <= 1) return true;
      for (let i = 1; i < periods.length; i++) {
        const prevEnd = toDateOrNull(periods[i - 1].endDate);
        const currStart = toDateOrNull(periods[i].startDate);
        if (prevEnd && currStart && currStart <= prevEnd) return false;
      }
      return true;
    },
    {
      path: ['periods'],
      message: 'Les périodes ne peuvent pas se croiser',
    },
  );

export type HotelPensionPricingFormData = z.infer<
  typeof HotelPensionPricingSchema
>;
export type HotelPensionPricingPeriodFormData = z.infer<
  typeof HotelPensionPricingPeriodSchema
>;
export type HotelPensionPriceFormData = z.infer<typeof HotelPensionPriceSchema>;
